/**
 * Project Workbook
 *
 * Multi-sheet workbook that generates Commitment Detail, Receivables Detail,
 * and Job Cost Detail on separate sheets, all from a single consolidated set
 * of Intacct queries.
 *
 * Instead of duplicating process logic, each sheet maps the shared datasets
 * to the keys expected by the existing report module, then calls its process()
 * directly.  Any future changes to the individual reports automatically flow
 * through to the workbook.
 *
 * Data sharing map (query once, use across sheets):
 *   APBILLITEM   -> Commitment + Receivables + Job Cost
 *   APBILL       -> Commitment (via dot-notation) + Receivables + Job Cost
 *   GLENTRY      -> Receivables + Job Cost
 *   GLBATCH      -> Receivables + Job Cost
 *   TIMESHEETENTRY -> Receivables + Job Cost
 *   TIMESHEET    -> Receivables + Job Cost
 *   PODOCUMENTENTRY -> Commitment + Receivables (source lookup)
 *   PODOCUMENT   -> Commitment + Receivables (source lookup)
 */

import commitmentDetailReport from './commitmentDetail';
import receivablesDetailReport from './receivablesDetail';
import jobCostDetail from './jobCostDetail';

const projectWorkbook = {
  id: 'project-workbook',
  name: 'Project Workbook',
  description: 'Generates a multi-sheet workbook for a single project: Commitment Detail, Receivables Detail, and Job Cost Detail. Queries shared data once to minimize API calls.',
  category: 'Workbooks',

  // This is a workbook, not a single report
  isWorkbook: true,

  parameters: [
    {
      id: 'projectId', label: 'Project', type: 'project-search',
      default: '',
      description: 'Select a project to generate the workbook for',
      required: true,
    },
    {
      id: 'includeClosed', label: 'Include Closed/Completed', type: 'checkbox',
      default: true, description: 'Include closed POs, contracts, etc.',
    },
    {
      id: 'includeStandaloneAR', label: 'Include Non-Contract AR', type: 'checkbox',
      default: true, description: 'Include AR invoices not tied to project contracts (Receivables sheet)',
    },
    {
      id: 'enableTimesheetTying', label: 'Tie Timesheets to Payroll', type: 'checkbox',
      default: false, description: 'Match PYRJ entries to timesheets for hours (Job Cost sheet)',
    },
  ],

  // Consolidated queries — superset of fields needed across all three reports.
  // Each object is queried ONCE; sheets share the results.
  queries: [
    // ========== COMMITMENT + RECEIVABLES: PO Lines ==========
    {
      id: 'podocumententry', object: 'PODOCUMENTENTRY',
      fields: [
        // Commitment needs
        'RECORDNO', 'DOCHDRNO', 'DOCHDRID', 'LINE_NO',
        'PROJECTID', 'PROJECTNAME',
        'COSTTYPEID', 'COSTTYPENAME', 'TASKID', 'TASKNAME',
        'VENDORID', 'VENDORNAME',
        'ITEMID', 'ITEMNAME', 'ITEMDESC', 'MEMO',
        'TRX_VALUE', 'TRX_REVISEDVALUE',
        'POSTEDCHANGEEXTPRICE', 'DRAFTCHANGEPRICE',
        'TOTAL_AMOUNT_CONVERTED', 'TOTAL_AMOUNT_REMAINING',
        'TRX_AMOUNTRETAINED', 'AMOUNTRETAINED',
        'PRICE_CONVERTED', 'QTY_CONVERTED', 'QTY_REMAINING',
        'PRIMARYDOCKEY', 'PRIMARYDOCLINEKEY',
        'STATE', 'STATUS', 'ADDEDBYCHANGE',
        'RECORD_URL', 'WHENCREATED',
      ],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "'",
    },

    // ========== COMMITMENT + RECEIVABLES: PO Headers ==========
    {
      id: 'podocument', object: 'PODOCUMENT',
      fields: [
        'RECORDNO', 'DOCID', 'DOCNO', 'PONUMBER',
        'CUSTVENDID', 'CUSTVENDNAME',
        'PROJECT', 'PROJECTNAME',
        'WHENCREATED', 'STATE', 'STATUS',
        'ORIGINALAMOUNT', 'REVISEDTOTAL', 'POSTEDCHANGESTOTAL',
        'TOTALENTERED', 'TOTALDUE', 'TOTALPAID',
        'MEGAENTITYID', 'RECORD_URL',
      ],
      buildFilter: (params) => "PROJECT = '" + params.projectId + "'",
    },

    // ========== SHARED: AP Bill Items (Commitment + Receivables + Job Cost) ==========
    {
      id: 'apbillitem', object: 'APBILLITEM',
      fields: [
        // Commitment needs
        'RECORDNO', 'RECORDKEY', 'APBILL.RECORDNO', 'APBILL.RECORDID', 'APBILL.DOCNUMBER',
        'APBILL.RECORD_URL', 'APBILL.MEGAENTITYID',
        'LINE_NO', 'AMOUNT', 'TRX_AMOUNT',
        'TOTALPAID', 'TRX_TOTALPAID',
        'TRX_AMOUNTRETAINED', 'TRX_AMOUNTRELEASED',
        'PRIMARYDOCKEY', 'PRIMARYDOCLINEKEY',
        'PROJECTID', 'PROJECTNAME',
        'COSTTYPEID', 'COSTTYPENAME', 'TASKID', 'TASKNAME',
        'VENDORID', 'VENDORNAME',
        'ENTRYDESCRIPTION', 'STATE', 'RECORD_URL', 'WHENCREATED',
        // Job Cost additionally needs
        'ACCOUNTNO', 'ENTRY_DATE',
        'DEPARTMENTID', 'LOCATIONID',
      ],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "'",
    },

    // ========== SHARED: AP Bill Headers (Receivables + Job Cost) ==========
    {
      id: 'apbill', object: 'APBILL',
      fields: [
        'RECORDNO', 'RECORDID', 'DOCNUMBER', 'RECORD_URL', 'MEGAENTITYID',
        'VENDORNAME', 'STATE',
      ],
      buildFilter: () => "STATE NOT IN ('Reversed','Reversal','Voided','Deleted')",
    },

    // ========== RECEIVABLES: Contract Lines ==========
    {
      id: 'projectcontractline', object: 'PROJECTCONTRACTLINE',
      fields: [
        'RECORDNO', 'PROJECTCONTRACTLINEID', 'PROJECTCONTRACTID', 'PROJECTCONTRACTNAME',
        'NAME', 'DESCRIPTION', 'BILLINGTYPE',
        'ORIGINALPRICE', 'TOTALREVISEDPRICE', 'BILLEDPRICE', 'BILLEDNETRETAINAGE',
        'BALANCETOBILL', 'RETAINAGEHELD', 'RETAINAGERELEASED', 'RETAINAGEBALANCE',
        'RETAINAGEPERCENTAGE',
        'TASKID', 'TASKNAME', 'COSTTYPEID', 'COSTTYPENAME',
        'PROJECTID', 'PROJECTNAME', 'CUSTOMERID', 'CUSTOMERNAME',
        'STATUS', 'RECORD_URL', 'MEGAENTITYID',
      ],
      buildFilter: (params) => {
        const parts = ["PROJECTID = '" + params.projectId + "'"];
        if (!params.includeClosed) parts.push("STATUS = 'active'");
        return parts.join(' AND ');
      },
    },

    // ========== RECEIVABLES: Invoice Lines (SODOCUMENTENTRY) ==========
    {
      id: 'sodocumententry', object: 'SODOCUMENTENTRY',
      fields: [
        'RECORDNO', 'PROJECTCONTRACTLINEKEY', 'PROJECTCONTRACTLINEID',
        'PROJECTCONTRACTID', 'PROJECTCONTRACTNAME',
        'DOCHDRNO', 'DOCHDRID', 'LINE_NO',
        'TRX_REVISEDVALUE', 'TOTAL', 'TRX_AMOUNTRETAINED', 'AMOUNTRETAINED',
        'COMPLETEDTHISPERIOD', 'PRIORAPPLICATIONAMT', 'TOTALCOMPLETEDTODATE',
        'COSTTYPEID', 'COSTTYPENAME', 'TASKID', 'TASKNAME',
        'PCBLBILLINGTYPE', 'ITEMDESC', 'ITEMID', 'ITEMNAME',
        'EMPLOYEEID', 'EMPLOYEENAME', 'QUANTITY',
        'BILLABLEGLENTRYKEY', 'BILLABLETIMEENTRYKEY',
        'BILLABLEDOCENTRYKEY', 'BILLABLEPRENTRYKEY',
        'PROJECTID', 'PROJECTNAME',
        'SODOCUMENT.DOCID', 'SODOCUMENT.MEGAENTITYID',
        'SODOCUMENT.WHENCREATED', 'SODOCUMENT.STATE',
      ],
      buildFilter: (params) => "PROJECTCONTRACTLINEKEY > 0 AND PROJECTID = '" + params.projectId + "'",
    },

    // ========== RECEIVABLES: Invoice Header URLs ==========
    {
      id: 'sodocument', object: 'SODOCUMENT',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: (params) => "PROJECT = '" + params.projectId + "'",
    },

    // ========== SHARED: GL Entries (Receivables + Job Cost) ==========
    {
      id: 'glentry', object: 'GLENTRY',
      fields: [
        'RECORDNO', 'BATCHNO', 'ACCOUNTNO', 'TR_TYPE', 'AMOUNT',
        'DESCRIPTION', 'ENTRY_DATE',
        'PROJECTID', 'PROJECTNAME', 'TASKID', 'TASKNAME',
        'COSTTYPEID', 'COSTTYPENAME',
        'EMPLOYEEID', 'EMPLOYEENAME',
        'VENDORID', 'VENDORNAME',
        'CUSTOMERID', 'CUSTOMERNAME',
        'DEPARTMENT', 'LOCATION',
        'CURRENCY', 'TRX_AMOUNT',
        'RECORD_URL', 'STATE',
      ],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "' AND STATE = 'Posted'",
    },

    // ========== SHARED: GL Batch Headers (Receivables + Job Cost) ==========
    {
      id: 'glbatch', object: 'GLBATCH',
      fields: [
        'RECORDNO', 'JOURNAL', 'BATCH_DATE', 'BATCH_TITLE',
        'RECORD_URL', 'MEGAENTITYID',
      ],
      buildFilter: () => "STATE = 'Posted'",
    },

    // ========== SHARED: Timesheet Entries (Receivables + Job Cost) ==========
    {
      id: 'timesheetentry', object: 'TIMESHEETENTRY',
      fields: [
        'RECORDNO', 'TIMESHEETKEY', 'DESCRIPTION', 'QTY', 'ENTRYDATE',
        'EMPLOYEEID', 'EMPLOYEENAME', 'COSTTYPEID', 'COSTTYPENAME',
        // Job Cost additionally needs
        'PROJECTID', 'TASKID', 'TIMETYPE', 'TS_ENDDATE', 'RECORD_URL',
      ],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "'",
    },

    // ========== SHARED: Timesheet Headers (Receivables + Job Cost) ==========
    {
      id: 'timesheet', object: 'TIMESHEET',
      fields: ['RECORDNO', 'RECORD_URL', 'MEGAENTITYID'],
      buildFilter: () => 'RECORDNO > 0',
    },

    // ========== JOB COST: GL Accounts (CoS filter) ==========
    {
      id: 'glaccount', object: 'GLACCOUNT',
      fields: ['ACCOUNTNO', 'TITLE', 'CATEGORY', 'ACCOUNTTYPE'],
      buildFilter: () => "CATEGORY LIKE 'Cost%' AND ACCOUNTTYPE = 'incomestatement' AND STATUS = 'active'",
    },

    // ========== JOB COST: Tasks (parent cost code hierarchy) ==========
    {
      id: 'task', object: 'TASK',
      fields: ['TASKID', 'NAME', 'PARENTID', 'PARENTTASKNAME', 'PROJECTID'],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "'",
    },

    // ========== RECEIVABLES: Standalone AR ==========
    {
      id: 'arinvoice', object: 'ARINVOICE',
      fields: [
        'RECORDNO', 'RECORDID', 'CUSTOMERID', 'CUSTOMERNAME',
        'WHENCREATED', 'WHENDUE', 'TOTALENTERED', 'TOTALDUE', 'TOTALRETAINED',
        'STATE', 'RECORD_URL', 'MEGAENTITYID', 'DESCRIPTION',
      ],
      buildFilter: () => "TOTALDUE != 0 AND STATE NOT IN ('Reversed','Reversal','Voided','Deleted')",
      conditional: (params) => !!params.includeStandaloneAR,
    },
    {
      id: 'arinvoiceitem', object: 'ARINVOICEITEM',
      fields: ['ARINVOICE.RECORDNO', 'PROJECTID', 'PROJECTNAME', 'AMOUNT', 'TOTALPAID', 'TRX_AMOUNTRETAINED'],
      buildFilter: (params) => "PROJECTID = '" + params.projectId + "'",
      conditional: (params) => !!params.includeStandaloneAR,
    },
  ],

  // ========== SHEET DEFINITIONS ==========
  // Each sheet maps the shared query results to the keys its report process() expects,
  // then delegates to the real report module.
  sheets: [
    {
      name: 'Commitment Detail',
      report: commitmentDetailReport,
      mapDatasets: (shared) => ({
        commitmentLines: shared.podocumententry || [],
        orderHeaders: shared.podocument || [],
        billLines: shared.apbillitem || [],
      }),
      mapParams: (wbParams) => ({
        projectFilter: wbParams.projectId,
        vendorFilter: '',
        includeFullyBilled: true,
        includeClosed: !!wbParams.includeClosed,
      }),
    },
    {
      name: 'Receivables Detail',
      report: receivablesDetailReport,
      mapDatasets: (shared) => ({
        contractLines: shared.projectcontractline || [],
        invoiceLines: shared.sodocumententry || [],
        soDocuments: shared.sodocument || [],
        glEntries: shared.glentry || [],
        glBatches: shared.glbatch || [],
        tsEntries: shared.timesheetentry || [],
        timesheets: shared.timesheet || [],
        apBillItems: shared.apbillitem || [],
        apBills: shared.apbill || [],
        poDocEntries: shared.podocumententry || [],
        poDocuments: shared.podocument || [],
        standaloneInv: shared.arinvoice || [],
        standaloneLines: shared.arinvoiceitem || [],
      }),
      mapParams: (wbParams) => ({
        projectId: wbParams.projectId,
        contractId: '',
        includeClosed: !!wbParams.includeClosed,
        includeStandalone: !!wbParams.includeStandaloneAR,
      }),
    },
    {
      name: 'Job Cost Detail',
      report: jobCostDetail,
      mapDatasets: (shared) => ({
        accounts: shared.glaccount || [],
        glEntries: shared.glentry || [],
        glBatches: shared.glbatch || [],
        apBillItems: shared.apbillitem || [],
        apBills: shared.apbill || [],
        tasks: shared.task || [],
        timesheetEntries: shared.timesheetentry || [],
        timesheets: shared.timesheet || [],
      }),
      mapParams: (wbParams) => ({
        projectFilter: wbParams.projectId,
        enableTimesheetTying: !!wbParams.enableTimesheetTying,
        timesheetDaysTolerance: '7',
      }),
    },
  ],
};

export default projectWorkbook;
